import { appPagePaths } from './navigation';
import { AppPage } from '../types';
import { persistence } from '../services/persistence';

type PersonalGrowthTab =
  | 'feedback'
  | 'health-actions'
  | 'listen'
  | 'health-literacy'
  | 'brain-training'
  | 'puzzles'
  | 'calendar';

interface PageMetadata {
  title: string;
  description: string;
  url?: string;
  image?: string;
  imageAlt?: string;
}

const BASE_URL = 'https://chat.eir.space';
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;
const DEFAULT_IMAGE_ALT = 'EIR Space — Know Your Health. Your AI-powered health companion. 100% Private.';

const baseDescription =
  'Your health is your life. EIR Space helps you understand your health records, track your wellbeing, and make informed decisions with AI-powered insights. 100% private — your data never leaves your device.';

const pageMetadataMap: Record<AppPage, PageMetadata> = {
  'ehr': {
    title: 'Health Records — EIR Space',
    description:
      'Your complete health story in one place. Search and understand your medical records with AI-powered insights. Know Your Health.'
  },
  'journal': {
    title: 'Health Journal — EIR Space',
    description:
      'Track how you feel day-to-day. Capture reflections, symptoms, and insights that clinical records miss. Know Your Health.'
  },
  'eir-chat': {
    title: 'AI Health Assistant — EIR Space',
    description:
      'Ask anything about your health. Get clear explanations of medical terms, test results, and personalized guidance. Know Your Health.'
  },
  'calendar': {
    title: 'Health Calendar — EIR Space',
    description:
      'Never miss an appointment or health task. Plan your care and stay on track. Know Your Health.'
  },
  'health-data': {
    title: 'Health Data — EIR Space',
    description:
      'See your health trends clearly. Visualize measurements, spot patterns, and understand your progress. Know Your Health.'
  },
  'health-assessment': {
    title: 'Health Assessments — EIR Space',
    description:
      'Understand where you stand. Quick assessments that surface risks and unlock personalized recommendations. Know Your Health.'
  },
  'health-programs': {
    title: 'Health Programs — EIR Space',
    description:
      'Structured programs to improve your mental and physical wellbeing. Evidence-based techniques like CBT that actually work. Know Your Health.'
  },
  'health-literacy': {
    title: 'Health Literacy — EIR Space',
    description:
      'Learn what matters for your health. Clear explanations of conditions, medications, and self-care. Know Your Health.'
  },
  'cbt-programs': {
    title: 'Health Programs — EIR Space',
    description:
      'Structured programs to improve your mental and physical wellbeing. Evidence-based techniques that actually work. Know Your Health.'
  },
  'health-map': {
    title: 'Health Map — EIR Space',
    description:
      'See where your care happens. Visualize providers, locations, and health events on an interactive map. Know Your Health.'
  },
  'health-videos': {
    title: 'Health Videos — EIR Space',
    description:
      'Expert guidance made simple. Watch videos that break down complex health topics. Know Your Health.'
  },
  'health-actions': {
    title: 'Health Actions — EIR Space',
    description:
      'Your daily health checklist. Track medications, lifestyle goals, and follow-ups that keep you healthy. Know Your Health.'
  },
  'care-coach': {
    title: 'Care Coach — EIR Space',
    description:
      'Prepare for appointments with confidence. AI coaching that helps you ask the right questions. Know Your Health.'
  },
  'nutrition': {
    title: 'Nutrition — EIR Space',
    description:
      'Connect what you eat to how you feel. Simple nutrition tracking for better health decisions. Know Your Health.'
  },
  'request-care': {
    title: 'Request Care — EIR Space',
    description:
      'Get the care you need faster. Share structured health summaries with your providers. Know Your Health.'
  },
  'feedback': {
    title: 'Feedback — EIR Space',
    description:
      'Help us build better health tools. Your input shapes the future of EIR Space. Know Your Health.'
  },
  'personal-growth': {
    title: 'Improve Your Health — EIR Space',
    description:
      'Tools to help you feel better every day. Health programs, brain training, and practical exercises. Know Your Health.'
  },
  'get-care': {
    title: 'Get Care — EIR Space',
    description:
      'Find the right care at the right time. Guided triage and preparation for your next steps. Know Your Health.'
  },
  'tutorial': {
    title: 'Getting Started — EIR Space',
    description:
      'Learn how EIR Space helps you take control of your health. Step-by-step guidance to get started. Know Your Health.'
  },
  'eir-docs': {
    title: 'Documentation — EIR Space',
    description:
      'Everything you need to know about EIR Space. Features, updates, and how it works. Know Your Health.'
  },
  'documentation': {
    title: 'Developer Docs — EIR Space',
    description:
      'Build with EIR Space. APIs, data formats, and technical documentation for developers. Know Your Health.'
  },
  'roadmap': {
    title: 'Roadmap — EIR Space',
    description:
      'See what\'s coming next. Our plans to make health understanding even better. Know Your Health.'
  },
  'for-you': {
    title: 'For You — EIR Space',
    description:
      'Your personalized health dashboard. Saved programs, exercises, and recommendations in one place. Know Your Health.'
  },
  'privacy': {
    title: 'Privacy — EIR Space',
    description:
      '100% private. Zero data retention. Your health data stays on your device, under your control, always. Know Your Health.'
  },
  'skills': {
    title: 'Skills Library — EIR Space',
    description:
      'Browse and add powerful skills to enhance your chat experience. Each skill combines an MCP server with a rich interface to help you achieve your health goals. Know Your Health.'
  },
  'health-records': {
    title: 'Health Records — EIR Space',
    description:
      'Your clinical health records and timeline. Know Your Health.'
  },
  'my-health': {
    title: 'My Health — EIR Space',
    description:
      'Your personal health dashboard. Know Your Health.'
  },
  'my-health-mental': {
    title: 'My Mental Health — EIR Space',
    description:
      'Understand and strengthen your mind. Know Your Health.'
  },
  'my-health-physical': {
    title: 'My Physical Health — EIR Space',
    description:
      'Track and improve your physical health. Know Your Health.'
  },
  'my-health-emotional': {
    title: 'My Emotional Health — EIR Space',
    description:
      'Build emotional awareness and resilience. Know Your Health.'
  },
  'my-health-social': {
    title: 'My Social Health — EIR Space',
    description:
      'Understand your relationship patterns. Know Your Health.'
  },
  'tracking': {
    title: 'Daily Tracking — EIR Space',
    description:
      'Track sleep, nutrition, activity, hydration, and bowel health. Build healthy habits with daily logging. Know Your Health.'
  }
};

const personalGrowthTabMetadata: Record<PersonalGrowthTab, PageMetadata> = {
  'feedback': {
    title: 'Personal Growth | Feedback Highlights',
    description:
      'Review reflections, gratitude, and peer feedback that encourage positive habits and reinforce your progress.'
  },
  'health-actions': {
    title: 'Personal Growth | Health Actions',
    description:
      'Stay accountable to daily habits, medications, and recovery milestones with clear, trackable actions.'
  },
  'listen': {
    title: 'Personal Growth | Guided Listening',
    description:
      'Listen to calming audio check-ins and guided prompts that help you decompress and stay mindful.'
  },
  'health-literacy': {
    title: 'Personal Growth | Health Literacy',
    description:
      'Build confidence with short explainers, Swedish-friendly resources, and AI summaries tailored to your needs.'
  },
  'brain-training': {
    title: 'Personal Growth | Brain Training',
    description:
      'Sharpen focus with adaptive n-back, memory, and attention drills inspired by cognitive science research.'
  },
  'puzzles': {
    title: 'Personal Growth | Health Puzzles',
    description:
      'Solve light, health-themed puzzles that combine curiosity, recall, and practical wellbeing tips.'
  },
  'calendar': {
    title: 'Personal Growth | Practice Calendar',
    description:
      'Keep a steady rhythm for brain training, CBT sessions, and listening exercises with a structured calendar.'
  }
};

const setMetaTag = (attribute: 'name' | 'property', key: string, value: string): void => {
  if (typeof document === 'undefined') return;

  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (existingTag) {
    existingTag.setAttribute('content', value);
    return;
  }

  const meta = document.createElement('meta');
  meta.setAttribute(attribute, key);
  meta.setAttribute('content', value);
  document.head.appendChild(meta);
};

const setLinkTag = (rel: string, href: string): void => {
  if (typeof document === 'undefined') return;

  const selector = `link[rel="${rel}"]`;
  const existingLink = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (existingLink) {
    existingLink.setAttribute('href', href);
    return;
  }

  const link = document.createElement('link');
  link.setAttribute('rel', rel);
  link.setAttribute('href', href);
  document.head.appendChild(link);
};

const buildPageUrl = (path: string, search: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const normalizedSearch = search ? search : '';
  return `${BASE_URL}${normalizedPath}${normalizedSearch}`;
};

interface ResolvedMetadata extends PageMetadata {
  url: string;
  image: string;
  imageAlt: string;
}

const resolvePersonalGrowthMetadata = async (searchParams: URLSearchParams): Promise<PageMetadata | null> => {
  const tab = searchParams.get('tab') as PersonalGrowthTab | null;

  if (!tab) return null;
  const tabMetadata = personalGrowthTabMetadata[tab];
  if (!tabMetadata) return null;

  return {
    ...tabMetadata,
    url: buildPageUrl(appPagePaths['personal-growth'], `?tab=${tab}`)
  };
};

const resolveHealthProgramsMetadata = async (searchParams: URLSearchParams): Promise<PageMetadata | null> => {
  const programId = searchParams.get('program');

  // If program parameter exists, try to load program-specific metadata
  if (programId) {
    try {
      const { loadCbtPrograms } = await import('../services/cbtPrograms');
      const programs = await loadCbtPrograms();
      
      // Also check user-created programs
      const userPrograms = persistence.getJSON<any[]>('user-created-cbt-programs', []);
      const allPrograms = [...programs, ...userPrograms];
      const program = allPrograms.find((p: any) => p.id === programId);

      if (program) {
        const programImage = program.image 
          ? (program.image.startsWith('http') ? program.image : `${BASE_URL}/cbt-programs/${program.image}`)
          : DEFAULT_IMAGE;

        return {
          title: `${program.title} | Health Programs | EIR Space`,
          description: program.summary || `A structured health program: ${program.title}`,
          image: programImage,
          imageAlt: program.title,
          url: buildPageUrl(appPagePaths['health-programs'], `?program=${programId}`)
        };
      }
    } catch (err) {
      console.error('Failed to load program metadata:', err);
    }
  }

  return null;
};

export const resolvePageMetadata = async (page: AppPage, searchParams: URLSearchParams): Promise<ResolvedMetadata> => {
  const baseMetadata = pageMetadataMap[page] ?? { title: 'EIR Space', description: baseDescription };
  
  let withTabMetadata = baseMetadata;
  if (page === 'personal-growth') {
    withTabMetadata = (await resolvePersonalGrowthMetadata(searchParams)) ?? baseMetadata;
  } else if (page === 'health-programs' || page === 'cbt-programs') {
    withTabMetadata = (await resolveHealthProgramsMetadata(searchParams)) ?? baseMetadata;
  }

  const url = withTabMetadata.url ?? buildPageUrl(appPagePaths[page], searchParams.toString() ? `?${searchParams.toString()}` : '');

  return {
    title: withTabMetadata.title,
    description: withTabMetadata.description || baseDescription,
    url,
    image: withTabMetadata.image || DEFAULT_IMAGE,
    imageAlt: withTabMetadata.imageAlt || DEFAULT_IMAGE_ALT
  };
};

export const applyPageMetadata = (metadata: ResolvedMetadata): void => {
  if (typeof document === 'undefined') return;

  document.title = metadata.title;
  setMetaTag('name', 'title', metadata.title);
  setMetaTag('name', 'description', metadata.description);

  setMetaTag('property', 'og:type', 'website');
  setMetaTag('property', 'og:site_name', 'EIR Space');
  setMetaTag('property', 'og:title', metadata.title);
  setMetaTag('property', 'og:description', metadata.description);
  setMetaTag('property', 'og:url', metadata.url);
  setMetaTag('property', 'og:image', metadata.image);
  setMetaTag('property', 'og:image:secure_url', metadata.image);
  setMetaTag('property', 'og:image:type', 'image/png');
  setMetaTag('property', 'og:image:alt', metadata.imageAlt);

  setMetaTag('property', 'twitter:card', 'summary_large_image');
  setMetaTag('property', 'twitter:site', '@eirspace');
  setMetaTag('property', 'twitter:title', metadata.title);
  setMetaTag('property', 'twitter:description', metadata.description);
  setMetaTag('property', 'twitter:image', metadata.image);
  setMetaTag('property', 'twitter:image:alt', metadata.imageAlt);
  setMetaTag('property', 'twitter:url', metadata.url);

  setLinkTag('canonical', metadata.url);
};
